import { fragSampler } from '../shaders/frag-sampler.shader';
import { vertSampler } from '../shaders/vert-sampler.shader';

import { RegionMask } from './components/regionMasks/regionMask';
import { BasePipeline, BaseUniformData, ShaderPropBufferData } from './pipeline-base';
import {
  CreateGlTextureByImageData,
  DeleteTextures,
  EnableVertexAttributeArray,
  GetUniformLocation,
  ResolveSamplerBindings,
  SetTextureUniform,
  TextureWrapMode
} from './utils/pipeline-utils-gl';
import { createMainSamplerBindings, createRegionMaskSamplerBindings, ResolvedSamplerBinding, SamplerBindingSpec } from './utils/shader-contract';

const VERT_TEXTURE_BUFFER_NAME = 'a_texCoord';

export class TexturePipelineBase extends BasePipeline {
  public glTextures: WebGLTexture[];
  protected renderingContextBase: TextureWrapMode;
  private flipYValue: number;
  /** When true, Destroy/CleanGlState will delete textures in glTextures. */
  private ownsGlTextures: boolean;

  // ! Here to be recognized by all classes of this type, even though it makes no sense to be on this class directly... (would never be used at this level)
  // TODO: I need to figure out better modularity for this whole pipeline system, including whether or not textures are even used.
  public regionMask?: RegionMask;

  public constructor(vertShader?: string, fragShader?: string) {
    super(vertShader || vertSampler, fragShader || fragSampler);

    this.glTextures = [];
    this.renderingContextBase = 0;
    this.flipYValue = 1;
    this.ownsGlTextures = false;
  }

  public get HasTexture(): boolean {
    return this.glTextures.length > 0;
  }

  public get FlipYValue(): number {
    return this.flipYValue;
  }

  public get TransformMatrix(): number[] {
    return [
      1, 0, 0, 0,
      0, this.FlipYValue, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1
    ];
  }

  public SetFlipY(flipped: boolean) {
    this.flipYValue = flipped ? -1 : 1;
  }

  protected OnContextAcquired(): void {
    this.renderingContextBase = this.gl.CLAMP_TO_EDGE;
  }

  protected getMainSamplerCount(): number {
    return 1;
  }

  protected getSamplerContract(): SamplerBindingSpec[] {
    const mainBindings = createMainSamplerBindings(this.getMainSamplerCount());
    const regionBindings = this.regionMask ? createRegionMaskSamplerBindings(mainBindings.length) : [];
    return [...mainBindings, ...regionBindings];
  }

  public CreateTextures(imgDataList: ImageData[]): WebGLTexture[] {
    if (imgDataList.length <= 0) {
      throw new Error(`TexturePipelineBase::CreateTextures called with an empty texture list`);
    }

    const w = imgDataList[0].width;
    const h = imgDataList[0].height;
    for (let i = 0; i < imgDataList.length; i++) {
      const data = imgDataList[i];
      if (data.width !== w || data.height !== h) {
        throw new Error(`CreateTextures called with multiple textures of different dimensions: ` +
          `index 0, w:<${w}>, h:<${h}>, index ${i}, width:<${data.width}>, height:<${data.height}>. ` +
          `Cannot operate within a single shader in this manner.`);
      }
    }

    const textures: WebGLTexture[] = [];
    for (const data of imgDataList) {
      const texture = CreateGlTextureByImageData(this.gl, data, this.renderingContextBase);
      textures.push(texture);
    }

    this.SetGlTextures(textures, true);
    return textures;
  }

  public SetGlTextures(textures: WebGLTexture[], takeOwnership = false) {
    if (this.ownsGlTextures) {
      this.DestroyGlTextures();
    }
    this.glTextures = textures;
    this.ownsGlTextures = takeOwnership;
  }

  public DestroyGlTextures(): void {
    if (this.ownsGlTextures && this.gl && this.glTextures.length > 0) {
      DeleteTextures(this.gl, this.glTextures);
    }
    this.glTextures = [];
    this.ownsGlTextures = false;
  }

  public override Destroy(): void {
    this.DestroyGlTextures();
    super.Destroy();
  }

  protected InitShaderProps(shaderProgram: WebGLProgram) {
    super.InitShaderProps(shaderProgram);

    const pipelineName = this.constructor.name;
    this.shaderProgramProps[VERT_TEXTURE_BUFFER_NAME] =
      EnableVertexAttributeArray(this.gl, shaderProgram, 'inTexCoord', pipelineName);
    this.shaderProgramProps.u_TransformMatrix =
      GetUniformLocation(this.gl, shaderProgram, 'uTransformMatrix', pipelineName);
    this.shaderProgramProps.u_samplerBindings =
      ResolveSamplerBindings(this.gl, shaderProgram, this.getSamplerContract(), pipelineName);

    this.regionMask?.GetProgramProps(this.gl, this.shaderProgramProps, shaderProgram, this.getMainSamplerCount(), pipelineName);
  }

  protected InitBuffers() {
    super.InitBuffers();

    const texCoords = this.GetTexCoords();
    this.InitTexCoordBuffer(texCoords);
  }

  protected GetTexCoords(): number[] {
    return [
      0.0, 0.0, // TL
      0.0, 1.0, // BL
      1.0, 1.0, // BR
      1.0, 0.0 // TR
    ];
  }

  protected InitTexCoordBuffer(texCoords: number[]) {
    this.buffers[VERT_TEXTURE_BUFFER_NAME] = {
      buffer: this.gl.createBuffer(),
      itemSize: 2,
      itemCount: texCoords.length / 2
    };
    const texBufferData: ShaderPropBufferData = this.buffers[VERT_TEXTURE_BUFFER_NAME];
    if (!texBufferData || texBufferData.buffer === null) {
      throw new Error(`${VERT_TEXTURE_BUFFER_NAME} does not exist!`);
    }
    const texBuffer: any = texBufferData.buffer;
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, texBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(texCoords), this.gl.STATIC_DRAW);
  }

  protected SetVertexAttributes() {
    if (!this.gl) { throw new Error('base does not have gl.'); }
    super.SetVertexAttributes();
    this.SetVertexAttribute(VERT_TEXTURE_BUFFER_NAME);
  }

  protected SetUniforms(data: BaseUniformData) {
    super.SetUniforms(data);

    this.gl.uniformMatrix4fv(this.shaderProgramProps.u_TransformMatrix, false, this.TransformMatrix);

    // * This will ensure that it's always in it's default state, and flip when told to, ahead of each given draw call.
    this.SetFlipY(false);

    const samplerBindings = this.shaderProgramProps.u_samplerBindings as ResolvedSamplerBinding[];
    for (let i = 0; i < this.glTextures.length; i++) {
      const texture = this.glTextures[i];
      const binding = samplerBindings[i];
      if (!binding) {
        throw new Error(
          `TexturePipelineBase::SetUniforms: Missing sampler binding for texture index <${i}> ` +
          `in pipeline <${this.constructor.name}>`
        );
      }
      SetTextureUniform(
        this.gl,
        this.gl.TEXTURE0 + binding.textureUnit,
        texture,
        binding.location,
        binding.textureUnit
      );
    }

    this.regionMask?.SetUniforms(this.gl, this.getMainSamplerCount(), this.shaderProgramProps);
  }

  public Draw(uniformData: BaseUniformData): void {
    if (!this.HasTexture) {
      throw new Error(`TexturePipeline::Draw called with <${this.glTextures.length}> textures cached to draw from`);
    }
    super.Draw(uniformData);
  }

  public DrawToTexture(uniformData: BaseUniformData): WebGLTexture {
    if (!this.gl) {
      // eslint-disable-next-line no-console
      console.trace('pipeline-textBase DrawToTexture error');
      throw new Error(`Expected this.gl to be defined, but it was ${this.gl}`);
    }
    this.SetFlipY(true);
    return super.DrawToTexture(uniformData);
  }

  public DrawOnceForPixelData(uniformData: BaseUniformData = {}): Uint8Array {
    if (this.glTextures.length === 0) {
      throw new Error(`TexturePipelineBase::DrawOnceForPixelData: No textures cached to get pixels of. At least 1 required here`);
    }

    return super.DrawOnceForPixelData(uniformData);
  }

  public CleanGlState() {
    this.DestroyGlTextures();

    if (!this.gl) {
      return;
    }

    super.CleanGlState();

    const numTextureUnits = this.gl.getParameter(this.gl.MAX_TEXTURE_IMAGE_UNITS);
    for (let unit = 0; unit < numTextureUnits; unit++) {
      this.gl.activeTexture(this.gl.TEXTURE0 + unit);
      this.gl.bindTexture(this.gl.TEXTURE_2D, null);
      this.gl.bindTexture(this.gl.TEXTURE_CUBE_MAP, null);
    }
  }
}
